import {
  ColDef,
  GetContextMenuItemsParams,
  GridOptions,
  MenuItemDef,
  Module,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import {
  RootState,
  selectActiveSessionReconDate,
} from '@helios/state-management'
import {
  DensityLevel,
  ModalPositionService,
  UserPreferencesService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath, formatDate, ISODateFormat } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  MMFAccountCharacteristics,
  MMFCharacteristics,
  MMFDividendCalendarTypes,
  MMFSettings,
} from '../../models/mmf-settings.model'
import { DistributionsService } from '../../services/distributions.service'
import { AddMmfCalendarComponent } from './add-mmf-calendar/add-mmf-calendar.component'

const mmfSettingPath = composeFieldPath([] as MMFAccountCharacteristics[])

@Component({
  selector: 'hs-mmf-settings-grid',
  templateUrl: './mmf-settings-grid.component.html',
  styleUrls: ['./mmf-settings-grid.component.scss'],
})
export class MmfSettingsGridComponent implements OnDestroy, OnInit {
  mmfsetting = {} as MMFSettings
  mmfAccountData: MMFAccountCharacteristics[] = []
  mmfDividendCalendarTypes: MMFDividendCalendarTypes[] = []
  paysOnLiquidationToggle = new FormControl(false)
  isLoading = false
  _securityId: number | undefined
  _accountId: number | undefined
  sessionReconDate?: Date

  @Input()
  set data(mmfSetting: MMFSettings | null) {
    if (mmfSetting) {
      this.setInitialData(mmfSetting)
    }
  }

  @Input()
  set securityId(securityId: number | null) {
    if (securityId) {
      this._securityId = securityId
    }
  }

  @Input() set accountId(accountId: number | null | undefined) {
    if (accountId) {
      this._accountId = accountId
    }
  }

  @Output() refreshDistribution = new EventEmitter<string>()

  readonly gridModules: Module[] = [FiltersToolPanelModule]
  readonly gridOptions: GridOptions = this.createGridOptions()
  readonly destroySubject$ = new Subject<void>()

  constructor(
    readonly changeDetector: ChangeDetectorRef,
    readonly preferences: UserPreferencesService,
    private readonly modalService: NgbModal,
    private readonly distributionsService: DistributionsService,
    private readonly store: Store<RootState>,
    public toastService: ToastService,
    private readonly modalPositionService: ModalPositionService,
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectActiveSessionReconDate)
      .subscribe(sessionReconDate => {
        this.sessionReconDate = sessionReconDate
      })

    this.paysOnLiquidationToggle.valueChanges.subscribe(() => {
      this.updateMmfSettings()
    })
  }

  setInitialData(mmfSetting: MMFSettings) {
    this.mmfsetting = mmfSetting
    this.mmfAccountData = mmfSetting?.mmfAccountCharacteristics
    this.mmfDividendCalendarTypes = mmfSetting?.mmfDividendCalendarTypes
    if (this.mmfAccountData[0]?.paysOnLiquidation) {
      this.paysOnLiquidationToggle.setValue(true)
    }
  }

  updateMmfSettings() {
    this.isLoading = true
    const updatedMmfList: MMFAccountCharacteristics[] = []
    const date = formatDate(this.sessionReconDate, ISODateFormat)
    if (
      this.mmfAccountData.length == 0 &&
      this._securityId &&
      this._accountId &&
      date &&
      this.paysOnLiquidationToggle.value
    ) {
      updatedMmfList.push({
        accountId: this._accountId,
        beginDate: date,
        securityId: this._securityId,
        paysOnLiquidation: this.paysOnLiquidationToggle.value,
        dividendCalendar: this.mmfDividendCalendarTypes[0].name,
      })
    } else
      this.mmfAccountData.forEach(mmfData => {
        updatedMmfList.push({
          beginDate: mmfData.beginDate,
          accountId: mmfData.accountId,
          securityId: mmfData.securityId,
          paysOnLiquidation: this.paysOnLiquidationToggle.value,
          dividendCalendar: mmfData.dividendCalendar,
        })
      })
    const newCalendar = {
      mmfCharacteristicUpdates: updatedMmfList,
    } as MMFCharacteristics

    if (newCalendar.mmfCharacteristicUpdates.length > 0)
      this.distributionsService.updateMmfCharacteristics(newCalendar).subscribe(
        () => {
          this.isLoading = false
          this.toastService.success('Calendar updated successfully')
          this.fetchMmfSettings()
        },
        () => {
          this.toastService.error('Error updating calendar')
          this.isLoading = false
        },
      )
    else {
      this.isLoading = false
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }
  createGridOptions(): GridOptions {
    return {
      columnDefs: this.createColumnDefs(),
      ...heliosClientSideRowConfig,
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        enableHeliosCopyBehavior(params.api)
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
    }
  }

  // @internal
  createColumnDefs(): ColDef[] {
    return [
      {
        field: mmfSettingPath('beginDate'),
        type: NfColumnTypes.Date,
        width: 180,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: mmfSettingPath('dividendCalendar'),
        width: 300,
      },
    ]
  }

  getContextMenuItems(params: GetContextMenuItemsParams) {
    const selectedCalendar =
      params?.node?.data ?? ({} as MMFAccountCharacteristics)
    const result: (string | MenuItemDef)[] = [
      {
        name: 'Delete Calendar',
        icon: '<span class="mdi mdi-delete-circle text-danger"></span>',
        action: () => {
          this.deleteCalendar(selectedCalendar)
        },
      },
    ]
    return result
  }

  deleteCalendar(selectedCalendar: MMFAccountCharacteristics) {
    this.isLoading = true
    const deleteCalendar = {
      mmfCharacteristicDeletes: [
        {
          accountId: selectedCalendar.accountId,
          beginDate: selectedCalendar.beginDate,
          securityId: selectedCalendar.securityId,
        },
      ],
    } as MMFCharacteristics
    if (deleteCalendar.mmfCharacteristicDeletes.length > 0)
      this.distributionsService
        .updateMmfCharacteristics(deleteCalendar)
        .subscribe(
          () => {
            this.fetchMmfSettings()
          },
          () => {
            this.toastService.error('Error deleting calendar')
            this.isLoading = false
          },
        )
    else {
      this.isLoading = false
    }
  }

  fetchMmfSettings() {
    if (this._securityId == null || this._accountId == null) {
      return
    }
    this.isLoading = true
    this.distributionsService
      .getMMFSettings(this._securityId, this._accountId)
      .subscribe(data => {
        this.isLoading = false
        this.mmfAccountData = data.mmfAccountCharacteristics
        this.refreshDistribution.emit('true')
      })
  }

  onRowDataChanged(event: RowDataChangedEvent) {
    if (this.mmfAccountData != null) {
      return
    }
    event.api.showLoadingOverlay()
  }

  async openMmfCalendar() {
    const compKey = 'mmf-calendar'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(AddMmfCalendarComponent, {
      size: 'sm',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.mmfData = {
      securityId: this._securityId,
      accountId: this._accountId,
      paysOnLiquidation: this.paysOnLiquidationToggle.value,
      existingMmfData: this.mmfAccountData,
      mmfDividendCalendarTypes: this.mmfDividendCalendarTypes,
    }

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    const isSuccess = await modalRef.result
    if (isSuccess) {
      this.fetchMmfSettings()
    }
  }
}
